import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import styled, { withTheme } from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLegend,
  VictoryLine,
  VictoryVoronoiContainer,
} from 'victory';

import { getDateLabel } from '../../../utils/dateLabel';
import LoadingCard from '../../LoadingCard';
import { NavArrowButton } from '../../Styled';
import StyledTitle from '../../Styled/StyledTitle';

import { CHART_DIMENSIONS } from './constants';
import CustomTooltip from './CustomTooltip';

const ChartHeaderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const SystemEnergyHistoryChart = ({
  data,
  fetchingAnalyticsData,
  yDomain,
  theme,
  onPreviousDay,
  onNextDay,
  isAtMinDate,
  isAtMaxDate,
  selectedDate,
}) => {
  const dataAvailable = !!data?.length;

  // Chart dimensions from constants
  const chartWidth = CHART_DIMENSIONS.WIDTH;
  const chartHeight = CHART_DIMENSIONS.HEIGHT;

  // Create a wrapper component that binds theme, data, and dataAvailable to CustomTooltip
  const BoundCustomTooltip = (props) => (
    <CustomTooltip
      {...props}
      data={data}
      dataAvailable={dataAvailable}
      theme={theme}
      width={chartWidth}
      height={chartHeight}
    />
  );
  BoundCustomTooltip.defaultEvents = CustomTooltip.defaultEvents;

  // Data keys representing different categories of energy data
  const dataKeys = ['Exported', 'Imported', 'Consumption', 'Solar generation'];
  // Colors corresponding to each data key using theme
  const colors = [
    theme.colors.exported, // Exported
    theme.colors.imported, // Imported
    theme.colors.success, // Consumption
    theme.colors.solarLight, // Solar generation
  ];

  // State to manage the highlighted key for emphasizing a specific data area
  const [highlightedKey, setHighlightedKey] = useState(null);

  // Get the date label for the title
  const dateLabel = getDateLabel(selectedDate);

  // Function to transform the data key name to have the first letter capitalized
  const transformDataName = (name) => name.charAt(0).toUpperCase() + name.slice(1);

  // Handle clicks on the legend to highlight the corresponding data area
  const handleLegendClick = (clickedDataKey) => {
    setHighlightedKey((prevKey) => (prevKey === clickedDataKey ? null : clickedDataKey));
  };

  // Prepare the data for the legend, applying highlighting based on the selected key
  const legendData = dataKeys.map((key, index) => {
    return {
      name: transformDataName(key),
      symbol: {
        fill: colors[index],
        type: highlightedKey === transformDataName(key) ? 'square' : 'none',
      },
    };
  });

  const placeholderData = [
    { time: '00:00', earnings: 0 },
    { time: '01:00', earnings: 0 },
    { time: '02:00', earnings: 0 },
    { time: '03:00', earnings: 0 },
    { time: '04:00', earnings: 0 },
    { time: '05:00', earnings: 0 },
    { time: '06:00', earnings: 0 },
    { time: '07:00', earnings: 0 },
    { time: '08:00', earnings: 0 },
    { time: '09:00', earnings: 0 },
    { time: '10:00', earnings: 0 },
    { time: '11:00', earnings: 0 },
    { time: '12:00', earnings: 0 },
    { time: '13:00', earnings: 0 },
    { time: '14:00', earnings: 0 },
    { time: '15:00', earnings: 0 },
    { time: '16:00', earnings: 0 },
    { time: '17:00', earnings: 0 },
    { time: '18:00', earnings: 0 },
    { time: '19:00', earnings: 0 },
    { time: '20:00', earnings: 0 },
    { time: '21:00', earnings: 0 },
    { time: '22:00', earnings: 0 },
    { time: '23:00', earnings: 0 },
    { time: '00:00', earnings: 0 },
  ];

  if (!dataAvailable) {
    yDomain = [-2, 2];
  }

  return (
    <>
      {/* Header section with navigation arrows and title */}
      <ChartHeaderNav style={{ marginTop: 40, marginBottom: 40 }}>
        <NavArrowButton
          onClick={onPreviousDay}
          disabled={isAtMinDate || fetchingAnalyticsData}
          aria-label="Previous day"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </NavArrowButton>

        <StyledTitle>System energy history {dateLabel}</StyledTitle>

        <NavArrowButton onClick={onNextDay} disabled={isAtMaxDate || fetchingAnalyticsData} aria-label="Next day">
          <FontAwesomeIcon icon={faChevronRight} />
        </NavArrowButton>
      </ChartHeaderNav>
      {/* Display loading card if data is being fetched */}
      {fetchingAnalyticsData && <LoadingCard />}
      {/* Render the chart when data is available */}
      {!fetchingAnalyticsData && (
        <div>
          <VictoryChart
            domainPadding={0}
            width={chartWidth}
            height={chartHeight}
            domain={{ y: yDomain }}
            padding={{ top: 30, right: 50, bottom: 50, left: 65 }}
            containerComponent={
              <VictoryVoronoiContainer
                voronoiDimension="x"
                labels={({ datum }) => {
                  if (!datum || !dataAvailable) return true;

                  // Get the data point for this time
                  const dataPoint = data.find((d) => d.time === datum.time);
                  if (!dataPoint) return true;

                  // Get absolute values and find the highest
                  const absValues = {
                    Exported: Math.abs(dataPoint.Exported || 0),
                    Imported: Math.abs(dataPoint.Imported || 0),
                    Consumption: Math.abs(dataPoint.Consumption || 0),
                    'Solar generation': Math.abs(dataPoint['Solar generation'] || 0),
                  };

                  const maxValue = Math.max(...Object.values(absValues));

                  // Find which series has the max value
                  const maxSeriesKey = Object.keys(absValues).find((k) => absValues[k] === maxValue);

                  // datum._y gives us the y-value, check which key this datum represents
                  // by comparing the value
                  const datumAbsValue = Math.abs(datum._y || 0);

                  // Only show tooltip if this series has the highest value
                  return datumAbsValue === maxValue;
                }}
                labelComponent={<BoundCustomTooltip />}
              />
            }
          >
            {/* Legend for data categories with click events for highlighting */}
            <VictoryLegend
              orientation="horizontal"
              gutter={20}
              style={{
                title: { fontSize: 14 },
                labels: { cursor: 'pointer' },
              }}
              data={legendData}
              events={[
                {
                  target: ['labels', 'data'],
                  eventHandlers: {
                    onClick: (_, props) => {
                      handleLegendClick(transformDataName(props.datum.name));
                    },
                  },
                },
              ]}
            />
            {/* X-axis representing time with custom tick formatting */}
            <VictoryAxis
              tickFormat={(t, index) => {
                // Display every 6th tick label for better readability
                if (index % 6 === 0) {
                  return t;
                }
                return '';
              }}
              style={{
                axis: { stroke: 'transparent' },
                ticks: { stroke: 'transparent', size: 5 },
                tickLabels: {
                  fontSize: 14,
                  padding: 5,
                  angle: -90,
                  textAnchor: 'end',
                },
                grid: { stroke: 'transparent' },
              }}
              offsetY={50}
            />
            {/* Y-axis representing energy values with custom formatting */}
            <VictoryAxis
              dependentAxis
              tickValues={dataAvailable ? undefined : [-2, -1, 0, 1, 2]}
              tickFormat={(t) => `${t} kWh`}
              style={{
                axis: { stroke: 'transparent' },
                ticks: { stroke: 'transparent' },
                tickLabels: {
                  fill: theme.colors.black,
                  fontSize: 14,
                  padding: 5,
                },
                grid: {
                  stroke: dataAvailable ? theme.colors.light : theme.colors.white,
                  opacity: 0.3,
                  strokeWidth: 0.5,
                  strokeDasharray: '0',
                },
                axisLabel: {
                  fontSize: 14,
                  padding: 30,
                  textAnchor: 'middle',
                  fill: theme.colors.black,
                  fontWeight: 'normal',
                },
              }}
            />

            {/* Reference line at y = 0 for baseline */}
            <VictoryLine
              style={{
                data: {
                  stroke: dataAvailable ? theme.colors.light : theme.colors.white,
                  strokeWidth: 1,
                },
              }}
              y={() => 0}
            />

            {/* Area charts for each data key representing energy flows */}
            {dataKeys.map((key, index) => (
              <VictoryArea
                key={key}
                name={key}
                data={dataAvailable ? data : placeholderData}
                x="time"
                y={key}
                style={{
                  data: {
                    fill: colors[index],
                    fillOpacity: highlightedKey === null || highlightedKey === transformDataName(key) ? 0.2 : 0.1, // Reduce opacity for non-highlighted areas
                    stroke: colors[index],
                    strokeWidth: highlightedKey === null || highlightedKey === transformDataName(key) ? 3 : 0, // Only show line for highlighted areas
                  },
                }}
              />
            ))}
            {!dataAvailable && (
              <>
                <VictoryLabel
                  textAnchor="middle"
                  style={{ fontSize: 15, fontWeight: 'bold' }}
                  x={'50%'}
                  y={190}
                  text="Not enough data to show System energy history."
                />
              </>
            )}
          </VictoryChart>
        </div>
      )}
    </>
  );
};

export default withTheme(SystemEnergyHistoryChart);
